import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
// #25511 — the old /case-metrics route redirects to /service-reports, query string kept.
import CaseMetricsRedirect from './pages/service-reports/CaseMetricsRedirect';
import { XCENTRAL_HUB_BASE, legacyHubRedirect } from './pages/loop-hub/XCentralHubNav';
import { AdminLayout } from './layouts/AdminLayout';
import { Skeleton } from '@xcentral/ui';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';
import { useAuth } from './auth/useAuth';
import { NoAccess } from './pages/NoAccess';
// Bug #22185 — the catch-all page for an unknown admin route. Eager: it is a small page that must
// render even when a chunk fails to load, and it is not worth its own chunk.
import { NotFound } from './pages/NotFound';
// Story #22201 (BR-MOB-013) — every account sub-page (contacts, departments, portal users, M365
// users, connected services, CSP licences, security posture) is a tab under the account header;
// each old page URL redirects to its tab (generalises Bug #21730's departments redirect,
// BR-DEPT-UI-005). Eager import: a two-line redirect is not worth a chunk.
import AccountSubpageRedirect from './pages/accounts/AccountSubpageRedirect';

// Lazy-loaded route pages — WP-A24 Foundation
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AccountList = lazy(() => import('./pages/accounts/AccountList'));
const AccountDetail = lazy(() => import('./pages/accounts/AccountDetail'));
const AccountCreate = lazy(() => import('./pages/accounts/AccountCreate'));
const ServiceTierList = lazy(() => import('./pages/config/ServiceTierList'));
const IncidentDomainList = lazy(() => import('./pages/config/IncidentDomainList'));
// Story #13729 — Capability-catalogue admin management surface (Epic #13714, Feature #13716 F2)
const CapabilityCatalogue = lazy(() => import('./pages/settings/CapabilityCatalogue'));
// Story #16405 — ProviderSkuMapping admin surface (Feature #16312). The map has been empty
// since migration 0221 deleted 0220's seed; this is the surface that decision assumed.
const ProviderSkuMappings = lazy(() => import('./pages/settings/ProviderSkuMappings'));
const RateCardList = lazy(() => import('./pages/ratecards/RateCardList'));
const RateCardCreate = lazy(() => import('./pages/ratecards/RateCardCreate'));
const RateCardDetail = lazy(() => import('./pages/ratecards/RateCardDetail'));
const AgreementList = lazy(() => import('./pages/agreements/AgreementList'));
const AgreementCreate = lazy(() => import('./pages/agreements/AgreementCreate'));
const AgreementDetail = lazy(() => import('./pages/agreements/AgreementDetail'));
const AgreementTemplateList = lazy(() => import('./pages/agreementtemplates/AgreementTemplateList'));
const AgreementTemplateCreate = lazy(() => import('./pages/agreementtemplates/AgreementTemplateCreate'));
const AgreementTemplateDetail = lazy(() => import('./pages/agreementtemplates/AgreementTemplateDetail'));
const OnboardingList = lazy(() => import('./pages/onboarding/OnboardingList'));
const OnboardingDetail = lazy(() => import('./pages/onboarding/OnboardingDetail'));
const OnboardingCreate = lazy(() => import('./pages/onboarding/OnboardingCreate'));
const TermsManagement = lazy(() => import('./pages/terms/TermsManagement'));
const AuditLogViewer = lazy(() => import('./pages/audit/AuditLogViewer'));

// WP-A25 M365/Procurement
const M365UserDetail = lazy(() => import('./pages/m365/M365UserDetail'));
const SecurityPostureHistory = lazy(() => import('./pages/securityposture/SecurityPostureHistory'));
const SecurityPostureAlerts = lazy(() => import('./pages/securityposture/SecurityPostureAlerts'));
const Pax8Accounts = lazy(() => import('./pages/procurement/Pax8Accounts'));
const PrismAccounts = lazy(() => import('./pages/procurement/PrismAccounts'));
// N7 (#14637) — PAX8 / PRISM account drill-down, reached by clicking a row on the lists above
const Pax8AccountDetail = lazy(() => import('./pages/procurement/Pax8AccountDetail'));
const PrismAccountDetail = lazy(() => import('./pages/procurement/PrismAccountDetail'));
// F3.18 C6 (#13937) — admin hardware catalogue browse/search
const HardwareCatalogue = lazy(() => import('./pages/procurement/HardwareCatalogue'));
// F3.18 S6a (#10322) — admin hardware PO cross-account list
const HardwarePoList = lazy(() => import('./pages/procurement/HardwarePoList'));
// F3.18 S6c (#13943) — admin hardware PO detail + lifecycle actions
const HardwarePoDetail = lazy(() => import('./pages/procurement/HardwarePoDetail'));
// F3.18 C7 (#13938) — admin hardware product detail
const HardwareProductDetail = lazy(() => import('./pages/procurement/HardwareProductDetail'));
// F3.18 S6b (#13942) — admin hardware PO create/edit
const HardwarePoEdit = lazy(() => import('./pages/procurement/HardwarePoEdit'));
// Bug #16220 — hardware supplier registry (the missing CREATE surface for dbo.HardwareSuppliers)
const HardwareSupplierList = lazy(() => import('./pages/procurement/HardwareSupplierList'));
// Bug #16218 — the shipped provider spend reports (#15504, #15506) had no portal caller at all.
const ProviderSpendReport = lazy(() => import('./pages/procurement/ProviderSpendReport'));
const ProviderDormantSpendReport = lazy(
  () => import('./pages/procurement/ProviderDormantSpendReport'),
);
// #11324 — Azure billing: the per-account Azure subscription register.
const AzureFleetPage = lazy(() => import('./pages/azure/AzureFleetPage'));
// Story #28451 — Cloud & Security › Tenants (BR-TEN-D-901). Tenant detail (`/tenants/:id`) is #28452.
const TenantsPage = lazy(() => import('./pages/tenants/TenantsPage'));
const ReconciliationRuns = lazy(() => import('./pages/reconciliation/ReconciliationRuns'));
const ReconciliationDiscrepancies = lazy(() => import('./pages/reconciliation/ReconciliationDiscrepancies'));
// Bug #16217 — the run-detail drill-down (the missing handoff off the runs list)
const ReconciliationRunDetail = lazy(() => import('./pages/reconciliation/ReconciliationRunDetail'));
const SyncStatusDashboard = lazy(() => import('./pages/sync/SyncStatusDashboard'));

// WP-A33 Notification UI
const NotificationPreferences = lazy(() => import('./pages/notifications/NotificationPreferences'));

// In-app help centre
const HelpCentre = lazy(() => import('./pages/help/HelpCentre'));

// WP-A17 Escalation
const EscalationDashboard = lazy(() => import('./pages/escalation/EscalationDashboard'));
// F3.42 Case Metrics dashboard — renamed Service Reports by #25511 (BR-C-SRPT-001)
const ServiceReports = lazy(() => import('./pages/service-reports/ServiceReports'));
// Email Triage (#15301) — the helpdesk@/support@ review queue as its own Service Desk route,
// replacing the Hub-tab link. Renders the existing EmailTriagePanel; the Hub tab is unchanged.
const EmailTriage = lazy(() => import('./pages/service-desk/EmailTriage'));
// Feedback & complaints (#18612) — the Service Desk register: NPS, CSAT and complaints.
// NOT the CaseEmailFeedbackPanel, which is the AI email-triage operator verdict.
const Feedback = lazy(() => import('./pages/service-desk/Feedback'));
// Change requests (#18610) — the Service Desk register of planned change awaiting approval.
const ChangeRequests = lazy(() => import('./pages/service-desk/ChangeRequests'));
// Work Orders (#18608) — the Service Desk queue of scheduled billable work.
const WorkOrders = lazy(() => import('./pages/service-desk/WorkOrders'));
// Client asset register (#18611) — every client device, by account (F3.17).
const AssetRegister = lazy(() => import('./pages/service-desk/AssetRegister'));
// Story #18613 — Service Desk / Central alerting: the cross-source alert register. Distinct from
// the Cloud & Security `security-posture/alerts` screen above, which is ONE source at a narrower
// scope and keeps its own route and contract.
const CentralAlerts = lazy(() => import('./pages/service-desk/CentralAlerts'));

// XCentral Sign — Audit Dashboard (#12204 §9.1)
const SignDashboard = lazy(() => import('./pages/sign/SignDashboard'));
// XCentral Sign — Envelopes + Templates (#12206 Sign-10)
const EnvelopeList = lazy(() => import('./pages/sign/EnvelopeList'));
const EnvelopeDetail = lazy(() => import('./pages/sign/EnvelopeDetail'));
const EnvelopeCreateWizard = lazy(() => import('./pages/sign/EnvelopeCreateWizard'));
const SignTemplates = lazy(() => import('./pages/sign/Templates'));

// WP-A26 Cases/Billing
const CaseList = lazy(() => import('./pages/cases/CaseList'));
const CaseCreate = lazy(() => import('./pages/cases/CaseCreate'));
const CaseDetail = lazy(() => import('./pages/cases/CaseDetail'));
const InvoiceList = lazy(() => import('./pages/invoices/InvoiceList'));
const InvoiceDetail = lazy(() => import('./pages/invoices/InvoiceDetail'));
const TimeEntryList = lazy(() => import('./pages/timeentries/TimeEntryList'));
// Story #28499 — the Monthly Billing Run; the Billing dashboard is its Invoice status section.
const MonthlyBillingRun = lazy(() => import('./pages/billing/MonthlyBillingRun'));

// Resource Planning (#6794)
const ResourcePlanningPage = lazy(() => import('./pages/resourceplanning/ResourcePlanningPage'));

// Integrations (Path Y — CSP routing). Xero mapping/connection moved into Company
// Management (#12983) — see pages/companies below.
const CspRoutingConfig = lazy(() => import('./pages/integrations/CspRoutingConfig'));
// Integrations — partner-level provider connection status + reconnect (#14638).
const IntegrationsProviders = lazy(() => import('./pages/integrations/IntegrationsProviders'));

// Company Management (#12983) — billing-entity CRUD + per-company Xero-org mapping +
// connection status. Supersedes the standalone Xero-settings mapping page.
const CompanyManagement = lazy(() => import('./pages/companies/CompanyManagement'));
// Internal Account Attribution (#14950) — the operator-set billing-entity -> internal-account
// mapping that internal time (leave, business development) attributes through. Deliberately its
// own screen rather than a Company Management tab: the API gates it to Owner/Operations only
// (AC6), narrower than Company Management's Owner,Operations,Admin.
const InternalAccountAttribution = lazy(() => import('./pages/companies/InternalAccountAttribution'));

// Marketing
const ContactLeadList = lazy(() => import('./pages/marketing/ContactLeadList'));
// Bug #22233 (BR-MOB-024) — one lead's own route: the phone detail page, or the split with it selected.
const ContactLeadRoute = lazy(() => import('./pages/marketing/ContactLeadList').then(m => ({ default: m.ContactLeadRoute })));

// Projects — #12276
const ProjectDetail = lazy(() => import('./pages/projects/ProjectDetail'));

// Deals — Feature #12172 (Piece 1, Story #12175 board, #12176 forecast)
const PipelineBoard = lazy(() => import('./pages/Deals/PipelineBoard'));
const Forecast = lazy(() => import('./pages/Deals/Forecast'));
// Story #20809 — the deal detail page (its own route; the drawer stays on the board).
const DealPage = lazy(() => import('./pages/Deals/DealPage'));

// Proposals — Story #12194 (unified admin list + read-only tracking page)
const ProposalList = lazy(() => import('./pages/proposals/ProposalList'));
const ProposalDetail = lazy(() => import('./pages/proposals/ProposalDetail'));
// Proposal builder — Story #12192 (DRAFT editor + preview + send)
const ProposalBuilder = lazy(() => import('./pages/proposals/ProposalBuilder'));

// Quotes + Orders — F3.6g (#10291)
const QuoteList = lazy(() => import('./pages/quotes/QuoteList'));
const QuoteCreate = lazy(() => import('./pages/quotes/QuoteCreate'));
const QuoteDetail = lazy(() => import('./pages/quotes/QuoteDetail'));
const OrderList = lazy(() => import('./pages/orders/OrderList'));
const OrderDetail = lazy(() => import('./pages/orders/OrderDetail'));

// Admin — Owner-only
const UsersRolesPage = lazy(() => import('./pages/admin/UsersRolesPage'));
const ContractorsPage = lazy(() => import('./pages/admin/ContractorsPage'));

// XCentral Hub cockpit — Owner/Admin/App (Story S1 #13554, Epic #13294; renamed by S1 #16040)
const LoopHubCockpit = lazy(() => import('./pages/loop-hub/LoopHubCockpit'));
// XCentral Hub PROJECT WORKSPACE — the tabbed project detail shell (Story S3 #16042). Was the
// project detail page (#15627/P2, Feature #13955); S3 re-shaped it into thirteen tabs. Carries its
// own Owner/Admin/App gate, same pattern as the cockpit above.
const LoopHubProjectWorkspace = lazy(() => import('./pages/loop-hub/ProjectWorkspace'));
// XCentral Hub MACHINE DETAIL — one build machine's recorded history (Bug #16244). The reader for a
// log that Story #15083 had been writing, and the retention job purging, with no consumer anywhere
// in the portal. Carries its own Owner/Admin/App gate, same pattern as the two above.
const LoopHubMachineDetail = lazy(() => import('./pages/loop-hub/MachineDetailPage'));
// XCentral Hub SESSION — one hub session full-screen (Story #22044). Owner/Admin/App, as the session routes are.
const SessionDetailRoute = lazy(() => import('./pages/loop-hub/sessions/SessionDetailRoute'));
// XCentral Hub NEW PROJECT — the In-Development panel until Story #21325's wizard mounts here (Bug #26089).
// Owner/Admin/App, the same gate as the hub routes above.
const LoopHubNewProject = lazy(() => import('./pages/loop-hub/NewProjectPage'));

// AI Oversight (#13703, #13705) — Owner/Operations/Admin. Story #13751 folded this into the (now
// retired) My Workspace page as a tab; Wave-1 B1 #14203 restores its own direct route since
// My Workspace no longer exists to host it (see AiOversightPage's own RBAC gate below).
const AiOversightPage = lazy(() => import('./pages/ai-oversight/AiOversightPage'));

// Hub — 3-tab shell (Cockpit / My Collaboration / My Work), Owner/Admin (Wave-1 A1 #14198).
// My Collaboration (B1 #14203) supersedes the retired /my-workspace page — Email Triage/Spam
// Viewer/Exec Inbox/Recently tracked fold into its unified two-pane list; Personal Contacts moved
// to a settings flyout (Topbar.tsx); AI Oversight is out of scope (not an email channel; see above).
// My Work (C1 #14208) is wired below.
const HubLayout = lazy(() => import('./pages/hub/HubLayout'));
const CockpitTab = lazy(() => import('./pages/hub/CockpitTab'));
const CollaborationTab = lazy(() => import('./pages/hub/CollaborationTab'));
const MyWorkTab = lazy(() => import('./pages/hub/MyWorkTab'));
const MyCalendarTab = lazy(() => import('./pages/hub/MyCalendarTab'));

// Weekly timesheet — split C1 (#15355). View + submit only; entries are created on the case /
// calendar-assign screens, never here. Lives under /my-workspace even though the old My Workspace
// PAGE was retired (#14203): the route pairs with its API surface (/api/v1/my-workspace/timesheet),
// and `/my-workspace` itself remains the redirect below — React Router ranks the more specific
// two-segment path above it, so both resolve correctly.
const WeeklyTimesheet = lazy(() => import('./pages/my-workspace/WeeklyTimesheet'));

// Timesheet compliance — split C3 (#15357). "Who has not done their timesheet": the cross-staff
// counterpart to the personal weekly timesheet above. Gated to Owner/Operations/Admin inside the
// page via useAuth, mirroring TimesheetsController's own class gate — this report names colleagues
// and says whether they complied, so it is not open to all staff.
const NotSubmittedReport = lazy(() => import('./pages/timesheets/NotSubmittedReport'));

// Story #23644 — BR-TIME-003 (B): non-billable time by staff and account, distinct from project
// utilisation. Gated to Owner/Operations/Admin inside the page via useAuth, mirroring
// UtilisationReportController's class gate; the API is the boundary.
const UtilisationReport = lazy(() => import('./pages/timeentries/UtilisationReport'));

// Monitoring (#18614) — Service Desk: what each monitored device has actually been doing.
// Deliberately declared HERE rather than beside the other Service Desk lazy imports at the head of
// this block: PRs !9071 (#18612 Feedback) and !9073 (#18613 Central alerting) are both open and
// both insert at that exact anchor, so a third insertion there would conflict with both for no
// reason. Nothing about this declaration depends on its position in the file.
const Monitoring = lazy(() => import('./pages/service-desk/Monitoring'));

// F11.5 self-service actions — foundation C (#20070). All five routes are registered here ONCE so
// each screen Story replaces exactly one page file under pages/self-service/ and never edits this
// router. Until then each page renders the shared In-Development state (operator-approved stub).
// No route-level role gate, matching every other admin route: the API is the boundary (spec §4 —
// reads StaffExceptApp, MSP approval InfraStaff) and navRegistry hides the links from other roles.
const ActionCatalogueListPage = lazy(() => import('./pages/self-service/ActionCatalogueListPage'));
const TenantActionConfigPage = lazy(() => import('./pages/self-service/TenantActionConfigPage'));
// F2.21 Guest domains (#23091) — under the Cloud & Security area's `/m365-tenants` owns entry; no nav item.
const GuestAllowedDomainsPage = lazy(() => import('./pages/identity/GuestAllowedDomainsPage'));
const TenantInvocationLogPage = lazy(() => import('./pages/self-service/TenantInvocationLogPage'));
const PendingApprovalsPage = lazy(() => import('./pages/self-service/PendingApprovalsPage'));
const PendingApprovalDetailPage = lazy(() => import('./pages/self-service/PendingApprovalDetailPage'));
// Story #10626 (BR-TPH-013) — the Teams on-call register (record only; no Teams call routing changes).
const OnCallPage = lazy(() => import('./pages/teams-phone/OnCallPage'));

function PageLoader() {
  return <div className="p-6"><Skeleton className="h-8 w-64 mb-6" /><Skeleton lines={5} className="h-4 w-full" /></div>;
}

function P({ children }: { children: React.ReactNode }) {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
    </RouteErrorBoundary>
  );
}

// #12983 declutter — /integrations/xero is retired in favour of /companies (Company
// Management now owns the Xero mapping + connection UI). This redirect preserves
// existing bookmarks/links AND the Xero OAuth callback's ?connect=success|error&detail=
// redirect (XeroIntegrationController hardcodes the /integrations/xero path) by carrying
// the query string across to the new route.
// Story #28499 — the Billing dashboard is now the Invoice status section of the Monthly Billing Run; an
// old /billing bookmark lands on the run.
function BillingRunRedirect() {
  const location = useLocation();
  return <Navigate to={`/billing/run${location.search}`} replace />;
}

function XeroSettingsRedirect() {
  const location = useLocation();
  return <Navigate to={`/companies${location.search}`} replace />;
}

// Wave-1 B1 #14203 — the retired /my-workspace page. Email Triage/Spam Viewer/Exec Inbox/
// Recently tracked now live in the Hub's My Collaboration tab; existing bookmarks/links redirect
// there rather than 404ing.
function MyWorkspaceRedirect() {
  return <Navigate to="/hub/collaboration" replace />;
}

// Story S1 #16040 (Epic #16035) — the hub's UI base moved `/loop-hub` -> `/xcentral-hub` with the
// "XCentral Hub" rename. Every pre-#16040 deep link redirects rather than 404s (AC6): the tab slugs
// survive the move unchanged, so `/loop-hub/approvals` -> `/xcentral-hub/approvals`, and an
// unrecognised old segment lands on Overview rather than a blank shell.
//
// THE `api/v1/loop-hub/*` REST PREFIX IS UNTOUCHED (AC3). Only the browser route moved — no client,
// machine or plugin call changes. Renaming the API prefix would break all seven fleet machines at
// once and needs a coordinated rollout.
function LoopHubSectionRedirect() {
  const { section } = useParams<{ section?: string }>();
  return <Navigate to={legacyHubRedirect(section)} replace />;
}

// The project page's own legacy link. `useParams` hands back a DECODED value, so the project name
// (which contains spaces — "BT001 - TSP - XCentral") is re-encoded on the way out.
function LoopHubProjectRedirect() {
  const { azdoProject } = useParams<{ azdoProject: string }>();
  return (
    <Navigate to={`${XCENTRAL_HUB_BASE}/projects/${encodeURIComponent(azdoProject ?? '')}`} replace />
  );
}

// Bug #19757 — while the entitlement is resolving, render neither the shell nor Access denied.
// Rendering the shell here (the old behaviour) showed an unentitled user the nav and fired the hub's
// API calls before the denial could land; rendering Access denied here would flash it at an
// authorised user. A neutral loader decides nothing.
function EntitlementLoader() {
  return (
    <div className="flex h-screen items-center justify-center bg-gray-50" role="status" aria-live="polite">
      <div className="text-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent mx-auto" />
        <p className="mt-4 text-sm text-gray-500">Checking access...</p>
      </div>
    </div>
  );
}

export function App() {
  // Bug #19757 — no entitlement at all (no roles on the API token, or AADSTS50105) gets Access
  // denied in place of the whole routed shell. A transient token failure is NOT a denial and falls
  // through to the shell exactly as before; a wrong role for one page is still that page's own
  // denial, inside the shell.
  const { entitlement } = useAuth();
  if (entitlement === 'loading') return <EntitlementLoader />;
  if (entitlement === 'noAccess') return <NoAccess />;

  return (
    <Routes>
      <Route element={<AdminLayout />}>
        <Route index element={<Navigate to="/hub" replace />} />
        {/* WP-A24 Foundation routes */}
        <Route path="dashboard" element={<P><Dashboard /></P>} />
        <Route path="accounts" element={<P><AccountList /></P>} />
        <Route path="accounts/new" element={<P><AccountCreate /></P>} />
        <Route path="accounts/:id" element={<P><AccountDetail /></P>} />
        {/* Story #22201 (BR-MOB-013) — retired account sub-page URLs redirect (replace) to their
            AccountDetail tab, so bookmarks keep working and Back never returns to them. */}
        <Route path="accounts/:id/contacts" element={<AccountSubpageRedirect tab="contacts" />} />
        <Route path="accounts/:id/departments" element={<AccountSubpageRedirect tab="departments" />} />
        <Route path="accounts/:id/portal-users" element={<AccountSubpageRedirect tab="portal-users" />} />
        <Route path="service-tiers" element={<P><ServiceTierList /></P>} />
        <Route path="incident-domains" element={<P><IncidentDomainList /></P>} />
        {/* Story #13729 — Capability-catalogue admin management surface (gate enforced inside
            the page via useAuth — Owner/App/Admin, mirrors CapabilitiesController's PUT) */}
        <Route path="settings/capability-catalogue" element={<P><CapabilityCatalogue /></P>} />
        {/* Story #16405 — provider-SKU map admin surface (gate enforced inside the page via
            useAuth — staff read floor, Owner/Infra/Admin to mutate, mirroring
            ProviderSkuMappingsController) */}
        <Route path="settings/provider-sku-mappings" element={<P><ProviderSkuMappings /></P>} />
        <Route path="rate-cards" element={<P><RateCardList /></P>} />
        <Route path="rate-cards/new" element={<P><RateCardCreate /></P>} />
        <Route path="rate-cards/:id" element={<P><RateCardDetail /></P>} />
        <Route path="agreements" element={<P><AgreementList /></P>} />
        <Route path="agreements/new" element={<P><AgreementCreate /></P>} />
        <Route path="agreements/:id" element={<P><AgreementDetail /></P>} />
        <Route path="agreement-templates" element={<P><AgreementTemplateList /></P>} />
        <Route path="agreement-templates/new" element={<P><AgreementTemplateCreate /></P>} />
        <Route path="agreement-templates/:id" element={<P><AgreementTemplateDetail /></P>} />
        <Route path="onboarding" element={<P><OnboardingList /></P>} />
        <Route path="onboarding/new" element={<P><OnboardingCreate /></P>} />
        <Route path="onboarding/:id" element={<P><OnboardingDetail /></P>} />
        <Route path="terms" element={<P><TermsManagement /></P>} />
        <Route path="audit-log" element={<P><AuditLogViewer /></P>} />

        {/* WP-A25 M365/Procurement routes */}
        <Route path="accounts/:accountId/m365-users" element={<AccountSubpageRedirect tab="m365-users" />} />
        <Route path="m365-users/:id" element={<P><M365UserDetail /></P>} />
        <Route path="accounts/:accountId/connected-services" element={<AccountSubpageRedirect tab="connected" />} />
        <Route path="accounts/:accountId/security-posture" element={<AccountSubpageRedirect tab="security" />} />
        {/* BR-MOB-013 — the posture history stays a separate page, reached from the Security tab and
            linking back to it. */}
        <Route path="accounts/:accountId/security-posture/history" element={<P><SecurityPostureHistory /></P>} />
        <Route path="security-posture/alerts" element={<P><SecurityPostureAlerts /></P>} />
        <Route path="pax8-accounts" element={<P><Pax8Accounts /></P>} />
        <Route path="prism-accounts" element={<P><PrismAccounts /></P>} />
        {/* N7 (#14637) — provider account drill-down. Both resolve to the Cloud & Security nav
            area via navRegistry's `/pax8-accounts` / `/prism-accounts` prefix ownership. */}
        <Route path="pax8-accounts/:id" element={<P><Pax8AccountDetail /></P>} />
        <Route path="prism-accounts/:id" element={<P><PrismAccountDetail /></P>} />
        <Route path="accounts/:accountId/csp-licenses" element={<AccountSubpageRedirect tab="licenses" />} />
        {/* F3.18 C6 (#13937) — admin hardware catalogue browse/search */}
        <Route path="hardware-catalogue" element={<P><HardwareCatalogue /></P>} />
        {/* Bug #16220 — hardware supplier registry: create/edit suppliers + run feed imports.
            No route-level role gate: the API read floor is all five internal roles and the page
            itself hides the mutate controls outside Owner/Operations/Admin. */}
        <Route path="hardware-suppliers" element={<P><HardwareSupplierList /></P>} />
        {/* Bug #16218 — the PAX8 spend reports (#15504, #15506) shipped RBAC-gated with no portal
            caller at all. No route-level role gate: the API enforces Owner/Operations/Admin on both
            endpoints and a 403 surfaces as the page's ErrorState. The `/dormant` child needs no
            navRegistry `owns` — it resolves to Cloud & Security through the `/provider-spend`
            prefix, the same reasoning the Procurement detail routes record. */}
        <Route path="provider-spend" element={<P><ProviderSpendReport /></P>} />
        <Route path="provider-spend/dormant" element={<P><ProviderDormantSpendReport /></P>} />
        {/* #11324 — Azure billing. No route-level role gate: the API enforces StaffExceptApp on the
            reads and InfraStaff on register/edit/unlink; a 403 surfaces as the page's ErrorState and
            the page hides the controls the caller may not use. */}
        <Route path="azure" element={<P><AzureFleetPage /></P>} />
        {/* Story #28451 — Tenants. No route-level role gate: the API enforces StaffExceptApp
            (BR-M365-A-905) and a 403 surfaces as the page's error state; navRegistry hides the link. */}
        <Route path="tenants" element={<P><TenantsPage /></P>} />
        {/* F3.18 S6a (#10322) — admin cross-account PO list. Declared BEFORE the `:id` and `new`
            routes below purely for readability; react-router ranks static segments above dynamic
            ones regardless of order, so `purchase-orders/new` still wins over `purchase-orders/:id`. */}
        <Route path="purchase-orders" element={<P><HardwarePoList /></P>} />
        {/* F3.18 S6c (#13943) — admin PO detail + send/order/receive */}
        <Route path="purchase-orders/:id" element={<P><HardwarePoDetail /></P>} />
        {/* F3.18 C7 (#13938) — admin hardware product detail */}
        <Route path="hardware-catalogue/:id" element={<P><HardwareProductDetail /></P>} />
        {/* F3.18 S6b (#13942) — admin hardware PO create/edit. Create is account-scoped (the PO
            hangs off an account); edit is by PO id. Both render the same screen. */}
        <Route path="accounts/:accountId/purchase-orders/new" element={<P><HardwarePoEdit /></P>} />
        <Route path="purchase-orders/new" element={<P><HardwarePoEdit /></P>} />
        <Route path="purchase-orders/:id/edit" element={<P><HardwarePoEdit /></P>} />
        <Route path="reconciliation" element={<P><ReconciliationRuns /></P>} />
        <Route path="reconciliation/discrepancies" element={<P><ReconciliationDiscrepancies /></P>} />
        {/* Bug #16217 — run detail. Two dynamic segments, so it cannot collide with the
            one-segment reconciliation/discrepancies route above. */}
        <Route path="reconciliation/:accountId/:runId" element={<P><ReconciliationRunDetail /></P>} />
        <Route path="sync" element={<P><SyncStatusDashboard /></P>} />
        {/* F11.5 self-service actions — foundation C (#20070). Unprefixed (spec D1). The two
            tenant-parameterised routes are reached from Account -> Connected services and the
            tenant picker, not the nav; navRegistry's Cloud & Security area owns `/m365-tenants`
            so they still resolve to that tab. */}
        <Route path="self-service-actions/catalogue" element={<P><ActionCatalogueListPage /></P>} />
        <Route path="self-service-actions/pending-approvals" element={<P><PendingApprovalsPage /></P>} />
        <Route path="self-service-actions/pending-approvals/:invocationId" element={<P><PendingApprovalDetailPage /></P>} />
        <Route path="m365-tenants/:m365TenantId/self-service-actions" element={<P><TenantActionConfigPage /></P>} />
        <Route path="m365-tenants/:m365TenantId/self-service-invocations" element={<P><TenantInvocationLogPage /></P>} />
        <Route path="m365-tenants/:m365TenantId/guest-allowed-domains" element={<P><GuestAllowedDomainsPage /></P>} />

        {/* Story #10626 — Teams on-call register (Cloud & Security; InfraStaff). */}
        <Route path="teams-phone/on-call" element={<P><OnCallPage /></P>} />

        {/* WP-A33 Notification UI */}
        <Route path="notification-preferences" element={<P><NotificationPreferences /></P>} />

        {/* WP-A17 Escalation */}
        <Route path="escalations" element={<P><EscalationDashboard /></P>} />

        {/* Resource Planning (#6794) */}
        <Route path="resource-planning" element={<P><ResourcePlanningPage /></P>} />

        {/* Monitoring (#18614) — Service Desk. Read is AllStaff (BR-MON-019), enforced at
            MonitoringController and re-checked in the page via useAuth. Placed beside
            resource-planning rather than beside case-metrics because !9071 and !9073 both insert
            at the case-metrics anchor while open. */}
        <Route path="monitoring" element={<P><Monitoring /></P>} />

        {/* Integrations — provider connections (#14638) + CSP routing (#11612).
            Xero moved to Company Management (#12983). */}
        <Route path="integrations/providers" element={<P><IntegrationsProviders /></P>} />
        <Route path="integrations/csp-routing" element={<P><CspRoutingConfig /></P>} />
        <Route path="integrations/xero" element={<XeroSettingsRedirect />} />

        {/* Company Management (#12983) — billing-entity CRUD + Xero org mapping/connection */}
        <Route path="companies" element={<P><CompanyManagement /></P>} />
        {/* Internal Account Attribution (#14950) — entity -> internal-account mapping (AC3) */}
        <Route path="companies/internal-attribution" element={<P><InternalAccountAttribution /></P>} />

        {/* XCentral Sign Audit Dashboard (#12204 §9.1) */}
        <Route path="sign" element={<P><SignDashboard /></P>} />
        {/* XCentral Sign — Envelopes + Templates (#12206 Sign-10) */}
        <Route path="sign/templates" element={<P><SignTemplates /></P>} />
        <Route path="sign/envelopes" element={<P><EnvelopeList /></P>} />
        <Route path="sign/envelopes/new" element={<P><EnvelopeCreateWizard /></P>} />
        <Route path="sign/envelopes/:id" element={<P><EnvelopeDetail /></P>} />

        {/* Email Triage (#15301) — gate enforced inside the page via useAuth
            (Owner/Operations/Admin, mirroring EmailReviewController) */}
        <Route path="email-triage" element={<P><EmailTriage /></P>} />
        <Route path="alerts" element={<P><CentralAlerts /></P>} />

        {/* Feedback & complaints (#18612) — read is AllStaff; the close control renders only for
            OperationsStaff, and the API re-checks it (BR-FBK-001). */}
        <Route path="feedback" element={<P><Feedback /></P>} />

        {/* Change requests (#18610) — read/raise/submit are AllStaff; the decision verbs render only for
            OperationsStaff, and the API re-checks every one (BR-CR-001). */}
        <Route path="change-requests" element={<P><ChangeRequests /></P>} />

        {/* Work Orders (#18608) — read is all staff plus Contractor (row-scoped by the API); the session verbs
            are all staff; create / engineers / submit / dispatch / cancel are OperationsStaff. The API re-checks
            every call (BR-A-F33-002, BR-WO-001/002). */}
        <Route path="work-orders" element={<P><WorkOrders /></P>} />

        {/* Client asset register (#18611) — read is all staff plus Contractor (account-scoped by the API, read only);
            event recording is Owner/Operations/Infra/Admin with Infra limited to Received/Deployed/Reassigned; every
            other write is OperationsStaff. The API re-checks every call (BR-A-F317-001..003, BR-AST-001). */}
        <Route path="service-desk/assets" element={<P><AssetRegister /></P>} />

        {/* WP-A26 Cases/Billing */}
        <Route path="service-reports" element={<P><ServiceReports /></P>} />
        <Route path="case-metrics" element={<CaseMetricsRedirect />} />
        <Route path="cases" element={<P><CaseList /></P>} />
        <Route path="cases/new" element={<P><CaseCreate /></P>} />
        <Route path="cases/:id" element={<P><CaseDetail /></P>} />
        <Route path="invoices" element={<P><InvoiceList /></P>} />
        <Route path="invoices/:id" element={<P><InvoiceDetail /></P>} />
        <Route path="time-entries" element={<P><TimeEntryList /></P>} />
        <Route path="time-entries/utilisation" element={<P><UtilisationReport /></P>} />
        <Route path="billing" element={<BillingRunRedirect />} />
        <Route path="billing/run" element={<P><MonthlyBillingRun /></P>} />

        {/* Marketing — Contact Leads triage */}
        <Route path="contact-leads" element={<P><ContactLeadList /></P>} />
        <Route path="contact-leads/:id" element={<P><ContactLeadRoute /></P>} />

        {/* Projects — #12276 */}
        <Route path="projects/:id" element={<P><ProjectDetail /></P>} />

        {/* Deal Pipeline — Feature #12172, Piece 1 (Story #12175 board, #12176 forecast) */}
        <Route path="deals" element={<P><PipelineBoard /></P>} />
        <Route path="deals/forecast" element={<P><Forecast /></P>} />
        {/* Story #20809 — /deals/:dealId. The static deals/forecast above always outranks this
            dynamic segment in React Router's route ranking, so the forecast is never read as a deal id. */}
        <Route path="deals/:dealId" element={<P><DealPage /></P>} />

        {/* Proposals — Story #12194 (unified admin list + read-only tracking page) */}
        <Route path="proposals" element={<P><ProposalList /></P>} />
        <Route path="proposals/:id" element={<P><ProposalDetail /></P>} />
        {/* Builder — Story #12192. A more specific path than `proposals/:id`, so React Router's
            ranking picks it; the tracking page above stays the SENT+ view. */}
        <Route path="proposals/:id/edit" element={<P><ProposalBuilder /></P>} />

        {/* Quotes + Orders — F3.6g (#10291) */}
        <Route path="quotes" element={<P><QuoteList /></P>} />
        <Route path="quotes/new" element={<P><QuoteCreate /></P>} />
        <Route path="quotes/:id" element={<P><QuoteDetail /></P>} />
        <Route path="orders" element={<P><OrderList /></P>} />
        <Route path="orders/:id" element={<P><OrderDetail /></P>} />

        {/* Admin — Owner-only (B3 #11842, C3 #11854 — gate enforced inside the page via useAuth) */}
        <Route path="users-roles" element={<P><UsersRolesPage /></P>} />
        <Route path="contractors" element={<P><ContractorsPage /></P>} />

        {/* XCentral Hub cockpit — Owner/Admin/App (Story S1 #13554, Epic #13294 — gate enforced
            inside the page via useAuth, same pattern as the Admin routes above). S1 #16040 renamed
            the base `/loop-hub` -> `/xcentral-hub` and registered all THIRTEEN hub pages; the
            `api/v1/loop-hub/*` REST prefix is deliberately unchanged (AC3). */}
        <Route path="xcentral-hub" element={<P><LoopHubCockpit /></P>} />
        {/* Every hub page is a real route so each is linkable and switching pages remounts its
            panel. `/xcentral-hub` is Overview; `:section` is one of the twelve registry slugs
            (XCentralHubNav.tsx) — an unknown segment redirects to Overview rather than 404ing. */}
        <Route path="xcentral-hub/:section" element={<P><LoopHubCockpit /></P>} />
        {/* One project's own workspace (Story #15627/P2; tabbed by S3 #16042). THREE segments, so
            React Router's ranking gives it precedence over the two-segment `xcentral-hub/:section`
            above — deep-linkable cold, which is the whole reason the drawer became a route.
            The bare URL is the Overview tab. */}
        {/* One build machine's recorded history (Bug #16244). Three segments, so it outranks the
            two-segment `xcentral-hub/:section` above by React Router's ranking — same precedent as
            the project workspace below. Deliberately NOT registered in XCENTRAL_HUB_ROUTES: that
            registry is the sidebar nav list, and the prototype's p-hub-machine states this surface
            is reached by clicking a row on Build Machines, not from the nav. */}
        <Route path="xcentral-hub/machines/:machineId" element={<P><LoopHubMachineDetail /></P>} />
        {/* Story #22044 — one hub session, full-screen (the phone's way in, BR-HUB-D-641). Registered once here so the
            session-page Story (#22046) adds only its slot file. Three segments, so it outranks `:section`. */}
        <Route path="xcentral-hub/sessions/:sessionId" element={<P><SessionDetailRoute /></P>} />
        {/* Bug #26089 AC2 — New project. A STATIC segment, so React Router ranks it above the dynamic
            `xcentral-hub/projects/:azdoProject` below and "new" is never read as a project name. */}
        <Route path="xcentral-hub/projects/new" element={<P><LoopHubNewProject /></P>} />
        <Route path="xcentral-hub/projects/:azdoProject" element={<P><LoopHubProjectWorkspace /></P>} />
        {/* Each of the thirteen workspace tabs is its own URL, so a tab is linkable and the back
            button works across tabs. An unknown `:tab` segment redirects to the project's Overview
            rather than rendering Overview under a URL that says otherwise (projectWorkspaceTabs.ts). */}
        <Route path="xcentral-hub/projects/:azdoProject/:tab" element={<P><LoopHubProjectWorkspace /></P>} />

        {/* Legacy `/loop-hub/*` deep links (#16040 AC6) — bookmarks, notification links and the
            operator's own muscle memory. Redirects, never 404s. Same three-vs-two segment ranking
            as above, so the project link keeps its own redirect. */}
        <Route path="loop-hub" element={<Navigate to={XCENTRAL_HUB_BASE} replace />} />
        <Route path="loop-hub/:section" element={<LoopHubSectionRedirect />} />
        <Route path="loop-hub/projects/:azdoProject" element={<LoopHubProjectRedirect />} />

        {/* Retired (Wave-1 B1 #14203) — Email Triage/Spam Viewer/Exec Inbox/Recently tracked
            now live in the Hub's My Collaboration tab. Redirects so existing bookmarks/links
            don't 404. */}
        <Route path="my-workspace" element={<MyWorkspaceRedirect />} />

        {/* Weekly timesheet — split C1 (#15355). A real page under the otherwise-redirected
            /my-workspace prefix; React Router's ranking gives this two-segment path precedence
            over the single-segment redirect above, so the redirect is unaffected. */}
        <Route path="my-workspace/timesheet" element={<P><WeeklyTimesheet /></P>} />

        {/* Timesheet compliance — split C3 (#15357). The role gate is enforced inside the page via
            useAuth (same pattern as the Admin routes), and again at the API boundary. */}
        <Route path="timesheets/not-submitted" element={<P><NotSubmittedReport /></P>} />

        {/* Hub — 3-tab shell (Cockpit / My Collaboration / My Work), Owner/Admin (Wave-1 A1
            #14198 — gate enforced inside HubLayout via useAuth, same pattern as the routes
            above). Cockpit is the index tab; My Collaboration (B1 #14203) and My Work (C1
            #14208) are both wired — no placeholders remain. */}
        <Route path="hub" element={<P><HubLayout /></P>}>
          <Route index element={<CockpitTab />} />
          <Route path="collaboration" element={<CollaborationTab />} />
          <Route path="work" element={<MyWorkTab />} />
          {/* #18501 — the fourth Hub tab. A nested route rather than a standalone page: the Hub is
              already the personal-work shell, and a separate top-level route would split the personal
              surface across two shells. */}
          <Route path="calendar" element={<MyCalendarTab />} />
        </Route>

        {/* AI Oversight (#13703, #13705) — Owner/Operations/Admin (gate enforced inside the page
            via useAuth). Story #13751 folded this into the (now retired) My Workspace page;
            Wave-1 B1 #14203 restores its own direct route since that host page no longer exists. */}
        <Route path="ai-oversight" element={<P><AiOversightPage /></P>} />

        {/* In-app help centre */}
        <Route path="help" element={<P><HelpCentre /></P>} />

        {/* Bug #22185 (BR-MOB-014) — the catch-all, INSIDE the AdminLayout route so an unknown path
            keeps the app shell. It must stay the last child: a defined route (and a record-level 404
            inside one, e.g. /accounts/<unknown id>) always wins over `*`. */}
        <Route path="*" element={<P><NotFound /></P>} />
      </Route>
    </Routes>
  );
}
